import { useState, useEffect, useCallback, useRef } from '@wordpress/element';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl } from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import { __ } from '@wordpress/i18n';
import FeedCTA from '@sb-blocks/feed-blocks/cta';
import FeedToolbar from '@sb-blocks/feed-blocks/toolbar';
import { getBlockById, buildCustomizerUrl, primaryButtonStyle } from '@sb-blocks/feed-blocks/utils';

/**
 * Create a Gutenberg Edit component for a Smash Balloon feed block.
 *
 * @param {Object} config
 * @param {string} config.id          Block identifier (e.g. 'twitter').
 * @param {string} config.blockName   Full block name (e.g. 'smashballoon/twitter-feed').
 * @param {string} config.localizeVar Window variable holding localized editor data.
 * @param {string} config.initFunction Window function to re-initialize the feed.
 */
export function createFeedBlockEdit({ id, blockName, localizeVar, initFunction }) {
	return (props) => {
		const { attributes, setAttributes } = props;
		const { feedId } = attributes;
		const blockRef = useRef(null);
		const blockProps = useBlockProps({ ref: blockRef });
		const block = getBlockById(id);

		const editorData = window[localizeVar] || {};
		const feeds = editorData.feeds || [];
		const feedUrl = editorData.feed_url || '';
		const isPro = editorData.is_pro_active || false;

		const feedsOptions = [
			{ label: __('Select a Feed', 'smashballoon'), value: '' },
			...feeds.map((feed) => ({
				label: feed.feed_name || `Feed ${feed.id}`,
				value: String(feed.id),
			})),
		];

		const [prevFeedId, setPrevFeedId] = useState(feedId);

		const initFeed = useCallback(() => {
			if (!blockRef.current) return;
			// Use ownerDocument.defaultView to get the correct window context.
			// In WP 7.0+ the block renders inside an iframe, so window !== iframe window.
			const doc = blockRef.current.ownerDocument;
			const win = doc.defaultView || window;
			if (typeof win[initFunction] === 'function') {
				setTimeout(() => win[initFunction](), 500);
				setTimeout(() => win[initFunction](), 1500);
				setTimeout(() => win[initFunction](), 3000);
			}
		}, []);

		useEffect(() => {
			if (feedId && feedId !== prevFeedId) {
				setPrevFeedId(feedId);
				initFeed();
			} else if (!feedId && prevFeedId) {
				setPrevFeedId('');
			}
		}, [feedId, prevFeedId, initFeed]);

		useEffect(() => {
			if (feedId) {
				initFeed();
			}
		}, []);

		return (
			<div {...blockProps}>
				<FeedToolbar id={id} attributes={attributes} />

				{feeds.length > 0 && (
					<InspectorControls>
						<PanelBody title={__('Feed Settings', 'smashballoon')}>
							<SelectControl
								label={__('Select a Feed', 'smashballoon')}
								value={feedId}
								options={feedsOptions}
								onChange={(value) => setAttributes({ feedId: value })}
							/>
						</PanelBody>
					</InspectorControls>
				)}

				{feedId ? (
					<ServerSideRender
						block={blockName}
						attributes={attributes}
						httpMethod="POST"
					/>
				) : (
					<FeedCTA
						id={id}
						feeds={feeds}
						feedsOptions={feedsOptions}
						createFeedUrl={feedUrl}
						isPro={isPro}
						attributes={attributes}
						setAttributes={setAttributes}
					/>
				)}
			</div>
		);
	};
}
